// src/modules/time-tracking/ShiftDetail.js (admin)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaTrash } from 'react-icons/fa';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import Modal from '../../components/shared/Modal';
import FeedbackModal from '../../components/shared/ModalNotification';
import { FormHeader, BackButton } from '../../components/styled/FormComponents';
import { getReportShift, deleteShift } from './timeTrackingService';
import ShiftView from './components/ShiftView';
import ShiftFormModal from './ShiftFormModal';

const Actions = styled.div`
  display: flex; gap: 10px; margin-left: auto;
  button { border: none; border-radius: ${(p) => p.theme.borderRadius}; cursor: pointer;
    padding: 10px 16px; display: flex; align-items: center; gap: 8px; font-weight: 500; }
  .edit { background: ${(p) => p.theme.colors.primary}; color: ${(p) => p.theme.colors.black}; }
  .del { background: transparent; color: ${(p) => p.theme.colors.error};
    border: 1px solid ${(p) => p.theme.colors.error}; }
`;

const ShiftDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getReportShift(id)
      .then(setDetail)
      .catch(() => setError('Failed to load shift.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (msg) => {
    setEditOpen(false);
    setFeedbackMsg(msg); setFeedbackSuccess(true); setShowFeedback(true);
    load();
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    try {
      await deleteShift(id);
      navigate('/time-tracking');
    } catch (e) {
      setFeedbackMsg(e.response?.data?.message || 'Failed to delete shift');
      setFeedbackSuccess(false); setShowFeedback(true);
    }
  };

  return (
    <DashboardLayout>
      <FormHeader>
        <BackButton onClick={() => navigate('/time-tracking')}><FaArrowLeft /> Back to Report</BackButton>
        <h2>Shift Review</h2>
        {detail?.shift && (
          <Actions>
            <button className="edit" onClick={() => setEditOpen(true)}><FaEdit /> Edit</button>
            <button className="del" onClick={() => setConfirmDelete(true)}><FaTrash /> Delete</button>
          </Actions>
        )}
      </FormHeader>
      {loading ? <LoadingSpinner /> : error ? <p style={{ color: '#FF5252' }}>{error}</p> : <ShiftView detail={detail} />}

      <ShiftFormModal
        isOpen={editOpen}
        mode="edit"
        shift={detail?.shift}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />

      {confirmDelete && (
        <Modal
          isOpen={confirmDelete}
          title="Delete Shift"
          message="Delete this shift? This cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      <FeedbackModal isOpen={showFeedback} message={feedbackMsg}
        success={feedbackSuccess} onComplete={() => setShowFeedback(false)} />
    </DashboardLayout>
  );
};

export default ShiftDetail;
